import { useEffect, useRef, useState } from "react";

import { useGetSadieSimulatorDefaults } from "@/hooks/queries/useGetSadieSimulatorDefaults";
import { useSimulateSadieDigest } from "@/hooks/mutations/useSimulateSadieDigest";
import { useSimulateSadieFit } from "@/hooks/mutations/useSimulateSadieFit";
import { useSimulateSadieTurn } from "@/hooks/mutations/useSimulateSadieTurn";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { sadieSimulator } from "@/lib/sadieSimulator";
import {
  SadieSimulatorDefaults,
  SadieSimulatorEntry,
  SadieSimulatorJobSearch,
  SadieSimulatorMatchedOpportunityFit,
  SadieSimulatorMessage,
  SadieSimulatorPostPipeline,
  SadieSimulatorSessionKind,
  SadieSimulatorWorkerInitiated,
} from "@/types";

// What a job search finds when Sadie asks for one in Session 3. The job is
// filled in from the fixtures once the defaults land.
export type SadieSimulatorSearchOutcome =
  | { status: "NONE" }
  | {
      status: "FOUND";
      opportunityKey?: string;
      previouslyShown?: boolean;
      priorReaction?: string;
    };

// Everything the simulator page does apart from drawing it: which worker,
// model, and prompt are selected, the transcript, and the turn in flight.
// Shared by the desktop and mobile pages so they cannot drift.
export function useSadieSimulator() {
  /* React Hooks */
  const [personaKey, setPersonaKey] = useState<string | undefined>(undefined);
  const [sessionKind, setSessionKind] =
    useState<SadieSimulatorSessionKind>("INTRO_LEARN");
  const [matchedOpportunityKey, setMatchedOpportunityKey] = useState<
    string | undefined
  >(undefined);
  const [openerInstructions, setOpenerInstructions] = useState<
    string | undefined
  >(undefined);
  // The route echoes back whatever opener wording it rendered with, so once
  // an override is applied the untouched default is gone from the response.
  // Remembered from the last render that ran without an override.
  const [defaultOpenerInstructions, setDefaultOpenerInstructions] = useState<
    string | undefined
  >(undefined);
  const [presetKey, setPresetKey] = useState<string | null>(null);
  const [model, setModel] = useState<string | null>(null);
  const [chunked, setChunked] = useState(false);
  const [instructions, setInstructions] = useState<string | null>(null);
  const [entries, setEntries] = useState<SadieSimulatorEntry[]>([]);
  // A finished Session 1 handed into Session 2: the digest the real digest
  // agent wrote from it, the last intent read, and the thread itself, kept
  // only to show beside what the digest kept of it. Session 2 never sees it.
  const [handoff, setHandoff] = useState<
    { digest: string; intentLevel: string | null } | undefined
  >(undefined);
  const [priorEntries, setPriorEntries] = useState<SadieSimulatorEntry[]>([]);
  // The fixture fit tags were written against the canned digests. After a
  // handoff the digest is new, so a judge model reads the jobs against it.
  const [liveFits, setLiveFits] = useState<
    Record<string, SadieSimulatorMatchedOpportunityFit> | undefined
  >(undefined);
  // Session 3 setup: what the worker has in motion, whether they ever
  // finished Session 1, and what a job search finds. The search is shared
  // with Session 4, which can also help a worker look again.
  const [pipelineKey, setPipelineKey] = useState<string | undefined>(undefined);
  // Session 4 setup: what the check-in is about.
  const [checkInKey, setCheckInKey] = useState<string | undefined>(undefined);
  const [session1Incomplete, setSession1Incomplete] = useState(false);
  const [searchOutcome, setSearchOutcome] =
    useState<SadieSimulatorSearchOutcome>({ status: "FOUND" });
  // Start with the search already done, so the job is in the prompt from the
  // worker's first text, instead of waiting for Sadie to ask for one.
  const [searchedAtStart, setSearchedAtStart] = useState(false);
  // Sadie asked for a search in this conversation, so the prompt now
  // carries what it found. Production runs at most one per session.
  const [searchRan, setSearchRan] = useState(false);
  // Between Sadie asking for a search and the same turn running again.
  const [isSearching, setIsSearching] = useState(false);
  // Armed right before a search-state change, so the render it produces is
  // spliced into the edited prompt instead of replacing it, and optionally
  // the turn that asked for the search runs again on the result.
  const pendingRender = useRef<{
    previousRender: string;
    edited: string;
    rerun?: { transcript: SadieSimulatorMessage[]; summary: string };
  } | null>(null);
  // The search to note on the next agent turn that lands. Kept until one
  // does, so a failed re-run keeps it for the retry.
  const pendingSearchNote = useRef<SadieSimulatorEntry["search"]>(undefined);
  // What the last turn was asked with, so a failed turn can be retried as is
  // rather than folded into whatever the worker types next.
  const lastTranscript = useRef<SadieSimulatorMessage[]>([]);
  // Bumped whenever the page moves on (reset, new worker, new session), so a
  // digest or fit call that started before the move is ignored when it lands
  // instead of applying stale state on top of the new setup.
  const handoffGeneration = useRef(0);

  const isWorkerInitiated = sessionKind === "WORKER_INITIATED";
  const isPostPipeline = sessionKind === "POST_PIPELINE";
  // The sessions where Sadie can ask for a job search mid-turn.
  const canSearch = isWorkerInitiated || isPostPipeline;
  const renderedJobSearch =
    canSearch && (searchedAtStart || searchRan)
      ? toJobSearch(searchOutcome)
      : undefined;
  const workerInitiated: SadieSimulatorWorkerInitiated | undefined =
    isWorkerInitiated
      ? {
          pipeline: pipelineKey,
          session1Incomplete,
          jobSearch: renderedJobSearch,
        }
      : undefined;
  const postPipeline: SadieSimulatorPostPipeline | undefined = isPostPipeline
    ? { checkIn: checkInKey, jobSearch: renderedJobSearch }
    : undefined;

  /* Database CRUD Operations */
  // Read
  const {
    data: defaults,
    isFetching: isLoadingDefaults,
    refetch: refetchDefaults,
    status,
  } = useGetSadieSimulatorDefaults(
    personaKey,
    sessionKind,
    matchedOpportunityKey,
    openerInstructions,
    handoff,
    workerInitiated,
    postPipeline,
  );
  // Run one turn
  const simulate = useSimulateSadieTurn();
  // Write the digest a finished Session 1 hands to Session 2
  const digestSim = useSimulateSadieDigest();
  // Grade the fixture jobs against a handed-off digest
  const fitSim = useSimulateSadieFit();

  // A fresh render of the defaults, on first load or after a worker switch,
  // becomes the editable prompt. Edits are kept until the worker changes.
  // A search-state change is the exception: its render is spliced into the
  // edits, and the turn that asked for the search runs again on it. A failed
  // load never reaches here, so a pending search waits for the retry.
  useEffect(() => {
    if (!defaults) return;
    const fresh = resolveDefaultInstructions(defaults, presetKey, chunked);
    const pending = pendingRender.current;
    pendingRender.current = null;
    if (pending) {
      const spliced = sadieSimulator.spliceRenderChange(
        pending.previousRender,
        fresh,
        pending.edited,
      );
      const next = spliced ?? fresh;
      setInstructions(next);
      if (pending.rerun) {
        pendingSearchNote.current = {
          summary: pending.rerun.summary,
          editsReplaced: spliced === null,
        };
        setIsSearching(false);
        runTurn(pending.rerun.transcript, next);
      }
    } else {
      setInstructions(fresh);
    }
    setModel((current) => current ?? defaults.defaultModel);
    setSearchOutcome((current) =>
      current.status === "FOUND" && !current.opportunityKey
        ? {
            ...current,
            opportunityKey: defaults.matchedOpportunities[0]?.key,
          }
        : current,
    );
    if (openerInstructions === undefined) {
      setDefaultOpenerInstructions(defaults.openerInstructions);
    }
    // Only a defaults change should replace the editor. Preset and chunk
    // changes update it through their own handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults]);

  /* Helpers */
  const persona = defaults?.personas.find(
    (p) => p.key === defaults.selectedPersona,
  );
  const matchedOpportunityOption = defaults?.matchedOpportunities.find(
    (m) => m.key === defaults.selectedMatchedOpportunity,
  );
  const preset = defaults ? resolvePreset(defaults, presetKey) : undefined;
  const defaultInstructions = defaults
    ? resolveDefaultInstructions(defaults, presetKey, chunked)
    : "";
  const activeInstructions = instructions ?? defaultInstructions;
  const activeModel = model ?? defaults?.defaultModel ?? "";
  const isInstructionsDirty =
    !!defaults && activeInstructions !== defaultInstructions;
  const isOpenerDirty =
    openerInstructions !== undefined &&
    openerInstructions !== defaultOpenerInstructions;
  const lastTurn = entries[entries.length - 1]?.turn;
  // Production hands a looped conversation to Session 1 on the same thread.
  // The simulator does not follow it there, so the conversation stops.
  const isLoopedToSession1 = lastTurn?.loopedToSession === "SESSION_1";
  const isEnded =
    !!lastTurn &&
    (lastTurn.disposition === "RESOLVE" ||
      lastTurn.optOut ||
      isLoopedToSession1);
  // Production nudges only off an opener that drew no reply, once. The page
  // offers it under exactly that condition: the opener is the whole thread.
  const followUpText = defaults?.followUpText ?? "";
  const isFollowUpAvailable =
    sessionKind === "PROACTIVE_PULSE" &&
    followUpText.length > 0 &&
    !isEnded &&
    entries.length === 1 &&
    entries[0]?.role === "assistant" &&
    // A silent opener sent nothing, so production would have no clock to run.
    !!entries[0].content.trim();
  // Production writes the digest at session close, from the whole thread,
  // so the handoff is only offered once Session 1 has actually resolved. An
  // opt-out also ends a session, but nothing follows it, so it does not count.
  const isHandoffAvailable =
    sessionKind === "INTRO_LEARN" &&
    entries.length > 0 &&
    lastTurn?.disposition === "RESOLVE" &&
    !lastTurn.optOut;

  // The instructions override is for a re-run on a render that has not
  // reached state yet.
  const runTurn = (
    transcript: SadieSimulatorMessage[],
    instructionsOverride?: string,
  ) => {
    if (!persona) return;
    if (sessionKind === "PROACTIVE_PULSE" && !matchedOpportunityOption) return;
    // While defaults refetch, `defaults` still holds the previous render, so a
    // turn sent now would run the prompt from before the change just made.
    if (isLoadingDefaults) return;
    lastTranscript.current = transcript;
    const turnInstructions = instructionsOverride ?? activeInstructions;
    simulate.mutate(
      {
        model: activeModel,
        instructions: turnInstructions,
        persona: {
          background: persona.background,
          userLocation: persona.userLocation,
          digest: persona.digest,
          intentLevel: persona.intentLevel,
        },
        transcript,
        sessionKind,
        matchedOpportunity: matchedOpportunityOption?.matchedOpportunity,
        workerInitiated,
        postPipeline,
      },
      {
        onSuccess: (turn) => {
          // Production sends nothing on this turn. It runs the search and
          // asks for the same turn again with what it found, so the page
          // does too: re-render with the result, then run it again.
          if (turn.needsJobSearch && canSearch && !renderedJobSearch) {
            pendingRender.current = {
              previousRender: defaultInstructions,
              edited: turnInstructions,
              rerun: { transcript, summary: describeSearch(searchOutcome) },
            };
            setIsSearching(true);
            setSearchRan(true);
            return;
          }
          const search = pendingSearchNote.current;
          pendingSearchNote.current = undefined;
          setEntries((prev) => [
            ...prev,
            {
              role: "assistant",
              content: turn.text,
              turn,
              ...(search ? { search } : {}),
            },
          ]);
        },
      },
    );
  };

  // What a search in this setup finds, for the note on the turn it ran for.
  const describeSearch = (outcome: SadieSimulatorSearchOutcome) => {
    if (outcome.status === "NONE") {
      return "Sadie searched and found nothing that fits";
    }
    const label =
      defaults?.matchedOpportunities.find(
        (m) => m.key === outcome.opportunityKey,
      )?.label ?? "a fixture job";
    return `Sadie searched and found: ${label}${outcome.previouslyShown ? " (shown before)" : ""}`;
  };

  // Drops a search this conversation ran. Setup changes re-render the
  // prompt anyway, so they drop it outright.
  const discardSearch = () => {
    pendingRender.current = null;
    pendingSearchNote.current = undefined;
    setSearchRan(false);
    setIsSearching(false);
  };

  // A conversation reset takes the prompt back to before the search, with
  // the edits carried over, the same way the search brought it forward.
  const rewindSearch = () => {
    const wasRendered = searchRan && !searchedAtStart;
    discardSearch();
    if (wasRendered) {
      pendingRender.current = {
        previousRender: defaultInstructions,
        edited: activeInstructions,
      };
    }
  };

  /* Handlers */
  const handleStart = () => {
    mixpanelUtils.trackButtonClick(
      "Send the opener",
      MIXPANEL_SCREENS.SADIE_SIMULATOR,
    );
    setEntries([]);
    runTurn([]);
  };

  const handleSend = (text: string) => {
    const next: SadieSimulatorEntry[] = [
      ...entries,
      { role: "user", content: text },
    ];
    setEntries(next);
    // The agent only ever sees text. A silent (pause) turn is left out of what
    // it is shown, the same way guac never persists an unsent message.
    runTurn(
      next
        .filter((e) => e.role === "user" || e.content.trim().length > 0)
        .map(({ role, content }) => ({ role, content })),
    );
  };

  const handleRetry = () => {
    runTurn(lastTranscript.current);
  };

  // Fixed copy, no model call, same as production. It lands in the transcript
  // as Sadie's message so the next real turn sees it in history.
  const handleFollowUp = () => {
    if (!isFollowUpAvailable) return;
    mixpanelUtils.trackButtonClick(
      "Send the follow-up",
      MIXPANEL_SCREENS.SADIE_SIMULATOR,
    );
    setEntries((prev) => [
      ...prev,
      { role: "assistant", content: followUpText },
    ]);
  };

  const invalidateHandoffWork = () => {
    handoffGeneration.current += 1;
    digestSim.reset();
    fitSim.reset();
  };

  // Grades the fixture jobs against a handed-off digest. The job list is the
  // same for every session kind, so the defaults in hand are fine to use.
  const runFitJudge = (digest: string) => {
    if (!defaults) return;
    const generation = handoffGeneration.current;
    setLiveFits(undefined);
    fitSim.mutate(
      {
        digest,
        opportunities: defaults.matchedOpportunities.map((m) => ({
          key: m.key,
          matchedOpportunity: m.matchedOpportunity,
        })),
      },
      {
        onSuccess: ({ fits }) => {
          if (generation !== handoffGeneration.current) return;
          setLiveFits(
            Object.fromEntries(
              fits.map(({ key, fit, note }) => [key, { fit, note }]),
            ),
          );
        },
      },
    );
  };

  const handleRetryFit = () => {
    if (handoff) runFitJudge(handoff.digest);
  };

  const handleReset = () => {
    invalidateHandoffWork();
    rewindSearch();
    setEntries([]);
    simulate.reset();
  };

  const handlePersonaChange = (key: string) => {
    invalidateHandoffWork();
    discardSearch();
    setPersonaKey(key);
    setHandoff(undefined);
    setPriorEntries([]);
    setLiveFits(undefined);
    setEntries([]);
    simulate.reset();
  };

  // A different session kind is a different goal pack entirely, so the
  // preset/instructions editor and any matched-opportunity choice reset with
  // it, same as a worker switch. Picking a session by hand also drops any
  // handoff, which only makes sense on the path that produced it.
  const handleSessionKindChange = (kind: SadieSimulatorSessionKind) => {
    invalidateHandoffWork();
    discardSearch();
    setSessionKind(kind);
    setMatchedOpportunityKey(undefined);
    setOpenerInstructions(undefined);
    setPresetKey(null);
    setHandoff(undefined);
    setPriorEntries([]);
    setLiveFits(undefined);
    setEntries([]);
    simulate.reset();
  };

  // The production handoff, on the page: the real digest agent reads the
  // finished Session 1, and Session 2 opens on that digest with the thread
  // left behind. The intent carried over is the last read Session 1 made.
  const handleContinueToPulse = () => {
    if (!isHandoffAvailable || !persona) return;
    mixpanelUtils.trackButtonClick(
      "Continue into Session 2",
      MIXPANEL_SCREENS.SADIE_SIMULATOR,
    );
    const transcript = entries
      .filter((e) => e.role === "user" || e.content.trim().length > 0)
      .map(({ role, content }) => ({ role, content }));
    const lastIntentRead = [...entries]
      .reverse()
      .find((e) => e.turn?.intentLevel)?.turn?.intentLevel;
    const generation = handoffGeneration.current;
    digestSim.mutate(
      { priorDigest: persona.digest, transcript },
      {
        onSuccess: ({ digest }) => {
          if (!digest || generation !== handoffGeneration.current) return;
          setPriorEntries(entries);
          setHandoff({ digest, intentLevel: lastIntentRead ?? null });
          setSessionKind("PROACTIVE_PULSE");
          setMatchedOpportunityKey(undefined);
          setOpenerInstructions(undefined);
          setPresetKey(null);
          setEntries([]);
          simulate.reset();
          runFitJudge(digest);
        },
      },
    );
  };

  // Every Session 3 or 4 setup change starts the conversation over, the same
  // as a job change in Session 2.
  const restartSetup = () => {
    discardSearch();
    setEntries([]);
    simulate.reset();
  };

  const handlePipelineChange = (key: string) => {
    setPipelineKey(key);
    restartSetup();
  };

  const handleSession1IncompleteChange = (value: boolean) => {
    setSession1Incomplete(value);
    restartSetup();
  };

  const handleSearchOutcomeChange = (outcome: SadieSimulatorSearchOutcome) => {
    setSearchOutcome(outcome);
    restartSetup();
  };

  const handleSearchedAtStartChange = (value: boolean) => {
    setSearchedAtStart(value);
    restartSetup();
  };

  const handleCheckInChange = (key: string) => {
    setCheckInKey(key);
    restartSetup();
  };

  const handleMatchedOpportunityChange = (key: string) => {
    setMatchedOpportunityKey(key);
    setEntries([]);
    simulate.reset();
  };

  // Re-renders the whole prompt server-side with the new opener wording
  // baked in, the same way a matched-opportunity or persona change does,
  // rather than splicing text client-side.
  const handleOpenerInstructionsChange = (value: string) => {
    setOpenerInstructions(value);
    setEntries([]);
    simulate.reset();
  };

  // Dropping the override refetches with none, which is the default render.
  const handleResetOpenerInstructions = () => {
    setOpenerInstructions(undefined);
    setEntries([]);
    simulate.reset();
  };

  // Picking a preset replaces the editor with that prompt. Edits are lost, the
  // same as a worker switch. The conversation keeps going, so a mid-thread
  // swap is possible on purpose.
  const handlePresetChange = (key: string) => {
    if (!defaults) return;
    setPresetKey(key);
    setInstructions(resolveDefaultInstructions(defaults, key, chunked));
  };

  // The toggle edits the prompt in place so the editor always shows exactly
  // what runs. Turning it off only removes the block it added.
  const handleChunkedChange = (next: boolean) => {
    if (!defaults) return;
    setChunked(next);
    setInstructions(
      next
        ? withChunking(activeInstructions, defaults.chunkingInstructions)
        : withoutChunking(activeInstructions, defaults.chunkingInstructions),
    );
  };

  const handleResetInstructions = () => {
    setInstructions(defaultInstructions);
  };

  return {
    activeInstructions,
    activeModel,
    chunked,
    defaultOpenerInstructions,
    defaults,
    entries,
    handleChunkedChange,
    handleContinueToPulse,
    handleFollowUp,
    handleMatchedOpportunityChange,
    handleOpenerInstructionsChange,
    handlePersonaChange,
    handlePresetChange,
    handleReset,
    handleResetInstructions,
    handleResetOpenerInstructions,
    handleRetry,
    handleRetryFit,
    handleSend,
    handleSessionKindChange,
    handleStart,
    isEnded,
    isFitError: fitSim.isError,
    isFitPending: fitSim.isPending,
    isFollowUpAvailable,
    isHandoffAvailable,
    isHandoffError: digestSim.isError,
    isHandoffPending: digestSim.isPending,
    isInstructionsDirty,
    isLoadingDefaults,
    isLoopedToSession1,
    isSearchPending: isSearching,
    isWorkerInitiated,
    // Canned fit tags describe the canned digest. Once a handoff digest is
    // in play only the judge's read applies, pending or not.
    isLiveFit: handoff !== undefined,
    isOpenerDirty,
    liveFits,
    isTurnError: simulate.isError,
    isTurnPending: simulate.isPending,
    matchedOpportunityOption,
    openerInstructions: openerInstructions ?? defaults?.openerInstructions,
    persona,
    preset,
    refetchDefaults,
    priorEntries,
    selectedMatchedOpportunity:
      matchedOpportunityKey ?? defaults?.selectedMatchedOpportunity,
    selectedPersona: personaKey ?? defaults?.selectedPersona,
    sessionKind,
    setInstructions,
    setModel,
    status,
    jobSearchSetup: {
      onSearchOutcomeChange: handleSearchOutcomeChange,
      onSearchedAtStartChange: handleSearchedAtStartChange,
      searchOutcome,
      searchedAtStart,
    },
    postPipelineSetup: {
      checkIns: defaults?.checkIns ?? [],
      onCheckInChange: handleCheckInChange,
      selectedCheckIn: checkInKey ?? defaults?.selectedCheckIn ?? "",
    },
    workerInitiatedSetup: {
      onPipelineChange: handlePipelineChange,
      onSession1IncompleteChange: handleSession1IncompleteChange,
      pipelines: defaults?.pipelines ?? [],
      selectedPipeline: pipelineKey ?? defaults?.selectedPipeline ?? "",
      session1Incomplete,
    },
  };
}

const toJobSearch = (
  outcome: SadieSimulatorSearchOutcome,
): SadieSimulatorJobSearch | undefined => {
  if (outcome.status === "NONE") return { status: "NONE" };
  if (!outcome.opportunityKey) return undefined;
  return {
    status: "FOUND",
    opportunityKey: outcome.opportunityKey,
    previouslyShown: outcome.previouslyShown,
    priorReaction:
      outcome.previouslyShown && outcome.priorReaction?.trim()
        ? outcome.priorReaction.trim()
        : undefined,
  };
};

const resolvePreset = (
  defaults: SadieSimulatorDefaults,
  presetKey: string | null,
) =>
  defaults.presets.find(
    (p) => p.key === (presetKey ?? defaults.defaultPreset),
  ) ?? defaults.presets[0];

const withChunking = (prompt: string, block: string) =>
  prompt.includes(block) ? prompt : `${prompt}\n\n${block}`;

const withoutChunking = (prompt: string, block: string) =>
  prompt.replace(`\n\n${block}`, "");

// The untouched prompt for a preset and toggle state. "Edited" and "Reset to
// default" are both measured against this.
const resolveDefaultInstructions = (
  defaults: SadieSimulatorDefaults,
  presetKey: string | null,
  chunked: boolean,
) => {
  const base = resolvePreset(defaults, presetKey).instructions;
  return chunked ? withChunking(base, defaults.chunkingInstructions) : base;
};
